import React, { useCallback, useMemo, useState } from 'react';
import { StyleSheet, Text, TouchableOpacity } from 'react-native';
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import FeatherIcon from 'react-native-vector-icons/Feather';
import { useTheme, ThemeColors } from '../context/ThemeContext';
import { Toast } from '../components/common/Toast';
import { WebPageShell } from '../components/common/WebPageShell';
import { ReportCardDetailView } from '../components/reportCards/ReportCardDetailView';
import { RootStackParamList } from '../types';

/** One published report card: in-app view + server PDF download (web). */
export function ReportCardDetailScreen() {
  const navigation = useNavigation<any>();
  const route = useRoute<RouteProp<RootStackParamList, 'ReportCardDetail'>>();
  const reportCardId = route.params?.reportCardId;
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);

  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const notify = useCallback((message: string, type: 'success' | 'error') => setToast({ message, type }), []);

  const goBack = () => {
    if (navigation.canGoBack()) navigation.goBack();
    else if (route.params?.returnTo === 'ExamResults') navigation.navigate('ExamResults');
    else navigation.navigate('Academics', { initialTab: 'Exams' });
  };

  return (
    <WebPageShell title="Report card" maxWidth={820}>
      <TouchableOpacity
        style={styles.backRow}
        onPress={goBack}
        accessibilityRole="link"
        accessibilityLabel="Back to exam results">
        <FeatherIcon name="arrow-left" size={16} color={colors.primary} />
        <Text style={styles.backText}>Back to exam results</Text>
      </TouchableOpacity>

      {reportCardId ? (
        <ReportCardDetailView key={reportCardId} reportCardId={reportCardId} scrollable={false} notify={notify} />
      ) : (
        <Text style={styles.emptyText}>Report card not found.</Text>
      )}

      <Toast
        visible={!!toast}
        message={toast?.message ?? ''}
        type={toast?.type ?? 'success'}
        onDismiss={() => setToast(null)}
      />
    </WebPageShell>
  );
}

const createStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    backRow: { flexDirection: 'row', alignItems: 'center', alignSelf: 'flex-start', gap: 8, marginBottom: 16, paddingVertical: 4 },
    backText: { fontSize: 14, fontWeight: '600', color: colors.primary },
    emptyText: { fontSize: 14, color: colors.textSecondary },
  });
